import React from 'react';
import { toast } from 'sonner';
import { Ban, Download, FilePlus2, Link2, Loader2, Pencil, RotateCw, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { adminApi } from '../../../lib/adminAuth';
import ClientPicker from './ClientPicker';
import ContractEditor from './ContractEditor';
import StatusPill from './StatusPill';
import { copyShareLink, downloadPdf, errMsg, fileName, fmtDate, inr } from './docUtils';

const FILTERS = ['all', 'draft', 'sent', 'signed', 'void'];

function NewContractDialog({ open, onClose, onCreated }) {
  const [clientId, setClientId] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const create = async () => {
    setBusy(true);
    try { const r = await adminApi.post('/api/admin/contracts', { client_id: clientId }); toast.success(`${r.data.contract_number} drafted`); onCreated(r.data._id); onClose(); }
    catch (e) { toast.error(errMsg(e, 'Could not create the contract')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-contract-dialog">
        <DialogHeader><DialogTitle>New contract</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">The draft is filled from the client&apos;s details and the Scope Library defaults. You can change everything before sending.</p>
        <ClientPicker value={clientId} onChange={setClientId} testId="new-contract-client" />
        <DialogFooter>
          <Button onClick={create} disabled={!clientId || busy} className="bg-gray-900 hover:bg-gray-800" data-testid="new-contract-create">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ContractsPanel({ settings, openId, onOpenHandled, canDelete, onRenew, refreshKey }) {
  const [rows, setRows] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [filter, setFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [autoSend, setAutoSend] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const openDoc = React.useCallback((id, send = false) => { setAutoSend(!!send); setEditing(id); }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.get('/api/admin/contracts', { params: { status: filter } }); setRows(r.data.contracts); setCounts(r.data.counts); }
    catch (e) { toast.error(errMsg(e, 'Could not load contracts')); }
    finally { setLoading(false); }
  }, [filter]);
  React.useEffect(() => { load(); }, [load, refreshKey]);
  React.useEffect(() => { if (openId) { openDoc(openId.id, openId.autoSend); onOpenHandled?.(); } }, [openId, onOpenHandled, openDoc]);
  const closeEditor = React.useCallback(() => setEditing(null), []);

  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errMsg(e, 'Action failed')); } };
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="soft-card p-4 sm:p-5" data-testid="contracts-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#1a1a2e]">Contracts</h3>
          <p className="text-xs text-slate-500">Drafted automatically when a client is added, or create one here. Clients sign online with a typed or drawn signature.</p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-gray-900 hover:bg-gray-800" data-testid="contracts-new"><FilePlus2 className="w-4 h-4 mr-1.5" /> New contract</Button>
      </div>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`contracts-filter-${f}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1)} <span className="opacity-60">{f === 'all' ? total : counts[f] || 0}</span>
          </button>
        ))}
      </div>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-600" /></div> : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500" data-testid="contracts-empty">No contracts here yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[720px]" data-testid="contracts-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="py-2 px-3">Contract</th><th className="py-2 px-3">Client</th><th className="py-2 px-3 text-right">Monthly fee</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3">Updated</th><th className="py-2 px-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c._id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`contract-row-${c._id}`}>
                  <td className="py-2.5 px-3"><button type="button" onClick={() => setEditing(c._id)} className="font-semibold text-slate-800 hover:text-fuchsia-700" data-testid={`contract-open-${c._id}`}>{c.contract_number}</button>
                    <p className="text-[11px] text-slate-400">v{c.version}{c.source === 'auto_onboarding' ? ' · auto' : c.source === 'proposal' ? ' · from proposal' : c.source === 'renewal' ? ' · renewal' : ''}{c.end_date ? ` · ends ${fmtDate(c.end_date)}` : ''}</p></td>
                  <td className="py-2.5 px-3">{c.brand_name || c.client_name}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{inr(c.fee)}</td>
                  <td className="py-2.5 px-3"><StatusPill status={c.status} testId={`contract-status-${c._id}`} />{c.first_viewed_at && c.status === 'sent' && <span className="ml-1 text-[10px] text-indigo-600">opened</span>}
                    {c.superseded && <span className="ml-1 text-[10px] font-semibold text-fuchsia-700" data-testid={`contract-renewed-${c._id}`}>renewed</span>}
                    {c.changed_since_sent && c.status === 'sent' && <span className="ml-1 text-[10px] font-semibold text-amber-700" data-testid={`contract-changed-${c._id}`}>changed since sent</span>}</td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">{fmtDate(c.signed_at || c.sent_at || c.updated_at)}</td>
                  <td className="py-2.5 px-3"><div className="flex justify-end gap-0.5">
                    <IconBtn title="Edit" onClick={() => setEditing(c._id)} testId={`contract-edit-${c._id}`}><Pencil /></IconBtn>
                    <IconBtn title="Download PDF" onClick={() => downloadPdf(`/api/admin/contracts/${c._id}/pdf`, fileName(c.contract_number))} testId={`contract-pdf-${c._id}`}><Download /></IconBtn>
                    {!['void'].includes(c.status) && <IconBtn title="Copy sign link" onClick={async () => { await copyShareLink(`/api/admin/contracts/${c._id}/share-link`); load(); }} testId={`contract-link-${c._id}`}><Link2 /></IconBtn>}
                    {c.status === 'signed' && !c.superseded && !c.open_renewal && onRenew && <IconBtn title="Renew" onClick={() => onRenew(c)} testId={`contract-renew-${c._id}`}><RotateCw /></IconBtn>}
                    {c.open_renewal && <button type="button" onClick={() => openDoc(c.open_renewal._id)} className="text-xs font-semibold text-fuchsia-700 px-1.5" data-testid={`contract-open-renewal-${c._id}`}>Renewal · {c.open_renewal.status}</button>}
                    {['sent', 'signed'].includes(c.status) && <IconBtn title="Void" onClick={() => window.confirm('Void this contract? The sign link will stop working.') && act(() => adminApi.post(`/api/admin/contracts/${c._id}/void`), 'Contract voided')} testId={`contract-void-${c._id}`}><Ban /></IconBtn>}
                    {c.status === 'draft' && canDelete && <IconBtn title="Delete draft" danger onClick={() => window.confirm('Delete this draft?') && act(() => adminApi.delete(`/api/admin/contracts/${c._id}`), 'Draft deleted')} testId={`contract-delete-${c._id}`}><Trash2 /></IconBtn>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewContractDialog open={creating} onClose={() => setCreating(false)} onCreated={(id) => { load(); openDoc(id); }} />
      <ContractEditor contractId={editing} onClose={closeEditor} onChanged={load} settings={settings} autoSend={autoSend} onOpenOther={openDoc} />
    </div>
  );
}

export function IconBtn({ children, title, onClick, testId, danger }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} data-testid={testId}
      className={`p-1.5 rounded-lg transition-colors [&>svg]:w-4 [&>svg]:h-4 ${danger ? 'text-rose-500 hover:bg-rose-50' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
      {children}
    </button>
  );
}
